import { useEffect, useState } from 'react';
import { Bell, X, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import usePushNotifications from '../hooks/usePushNotifications';

const DISMISS_KEY = 'ccuk_push_prompt_dismissed_at';
// How long the user gets a break after dismissing. We deliberately do NOT
// re-show every session — Wayne explicitly asked us not to nag. 14 days
// is a fair compromise between "respect the user" and "remind them once
// they've forgotten they ever said no".
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;
// Don't fire on the FIRST page-load after login either — let the user
// orient first. Show the card after a short delay of being inside the
// authenticated shell.
const FIRST_SHOW_DELAY_MS = 4_000;

/** Detect iOS Safari context — push only works on iOS 16.4+ AND only
 *  when the app is installed to the Home Screen (standalone display
 *  mode). Anything else needs install guidance, not a permission
 *  button that would do nothing.
 */
function detectIosContext() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return { isIos: false, standalone: false, inAppBrowser: false };
  }
  const ua = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator?.standalone === true;
  // Detect in-app browsers (Facebook / Instagram / TikTok / WeChat /
  // Line / Twitter) which can't enable push. We show fallback guidance.
  const inAppBrowser = /(FBAN|FBAV|Instagram|TikTok|MicroMessenger|Line\/|Twitter)/i.test(ua);
  return { isIos, standalone, inAppBrowser };
}

function shouldShowPrompt({ user, supported, permission, subscribed }) {
  if (!user) return false;             // never to logged-out visitors
  if (permission === 'granted' && subscribed) return false; // already on
  if (permission === 'denied') return false; // can't recover from JS — leave them alone
  if (!supported) {
    // For unsupported (mostly iOS Safari outside standalone) we still
    // want to surface install guidance once. The card handles that
    // path with the iOS-specific copy below.
  }
  try {
    const stamp = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (stamp && Date.now() - stamp < DISMISS_COOLDOWN_MS) return false;
  } catch { /* private mode */ }
  return true;
}

/**
 * Soft, post-login prompt asking the user to enable browser push.
 * Single-instance — mounted from Layout for logged-in users. Respects
 * the user's choice for 14 days. Shows the right message for the
 * current platform (Android-supported → real button; iOS without
 * Home Screen → install guidance; iOS in-app browser → "open in
 * Safari" guidance).
 */
export default function PushNotificationsPrompt() {
  const { user } = useAuth();
  const { supported, permission, subscribed, pending, subscribe } = usePushNotifications();
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState({ isIos: false, standalone: false, inAppBrowser: false });

  useEffect(() => {
    setIos(detectIosContext());
  }, []);

  useEffect(() => {
    // Re-evaluate visibility whenever the upstream state changes (e.g.
    // after the user subscribes the card should disappear immediately).
    if (!shouldShowPrompt({ user, supported, permission, subscribed })) {
      setVisible(false);
      return undefined;
    }
    // Delay first show so the user isn't ambushed mid-redirect.
    const t = setTimeout(() => setVisible(true), FIRST_SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [user, supported, permission, subscribed]);

  if (!visible) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  const onEnable = async () => {
    const res = await subscribe();
    if (res.ok) {
      toast.success('Push notifications enabled — you\'ll hear about new messages and updates.');
      setVisible(false);
      return;
    }
    if (res.reason === 'permission_denied') {
      toast.error('Permission blocked. You can re-enable push in your browser site settings.');
      dismiss();
    } else if (res.reason === 'server_not_configured') {
      toast.error('Push notifications aren\'t set up on the server yet. Try again later.');
      dismiss();
    } else if (res.reason === 'unsupported') {
      toast.error('Your browser doesn\'t support push notifications.');
      dismiss();
    } else {
      toast.error(`Could not enable push: ${res.reason || 'unknown error'}.`);
    }
  };

  // ── Pick the right card variant ──────────────────────────────────
  // Order matters: in-app browser > iOS-without-standalone > supported.
  const isInAppBrowserOniOs = ios.isIos && ios.inAppBrowser;
  const isIosWithoutInstall = ios.isIos && !ios.standalone && !ios.inAppBrowser;
  const isHappyPath = supported && !isInAppBrowserOniOs && !isIosWithoutInstall;

  return (
    <div
      // Mobile: sits ABOVE the bottom mobile nav (z-50, ~80px tall) and
      //         respects iPhone home-indicator safe area via the inline
      //         CSS variable. Card pinned ~5.5rem above the bottom edge
      //         so the dismiss/enable buttons are fully tappable.
      // Desktop (md+): unchanged — bottom-right floating card 1.5rem
      //         from the corner; the inline `--ccuk-prompt-bottom` is
      //         overridden by the `md:bottom-6` Tailwind class because
      //         that class wins on `md+` breakpoints.
      // z-[55] keeps the card above MobileNav (z-50) without clashing
      // with full-screen modals (z-[60]+ in shadcn dialogs).
      className="fixed left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-[55] rounded-xl border border-theme bg-theme-surface shadow-2xl p-4 bottom-[var(--ccuk-prompt-bottom)] md:bottom-6"
      style={{
        '--ccuk-prompt-bottom': 'calc(5.5rem + env(safe-area-inset-bottom, 0px))',
      }}
      role="region"
      aria-label="Enable push notifications"
      data-testid="push-prompt-card"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 shrink-0 rounded-full bg-[#002FA7]/15 flex items-center justify-center">
          <Bell className="h-4 w-4 text-[#002FA7]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-theme-text mb-1">
            Turn on notifications
          </p>

          {isInAppBrowserOniOs && (
            <p className="text-xs text-theme-muted">
              Push notifications can&apos;t be enabled inside this app browser.
              Tap the share icon and choose <strong>Open in Safari</strong>,
              then return here.
            </p>
          )}

          {isIosWithoutInstall && (
            <div className="text-xs text-theme-muted space-y-1.5">
              <p>
                On iPhone, add Casting Calls UK to your Home Screen first,
                then open it from the app icon to enable push notifications.
              </p>
              <p className="flex items-center gap-1.5">
                <Smartphone className="h-3.5 w-3.5" />
                Tap the share icon → <em>Add to Home Screen</em>.
              </p>
            </div>
          )}

          {isHappyPath && (
            <p className="text-xs text-theme-muted">
              So you don&apos;t miss messages, casting updates or reader
              session requests.
            </p>
          )}

          {!supported && !ios.isIos && (
            <p className="text-xs text-theme-muted">
              Your browser doesn&apos;t support push notifications. You&apos;ll
              still receive in-app alerts when the site is open.
            </p>
          )}

          <div className="mt-3 flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={dismiss}
              data-testid="push-prompt-dismiss"
            >
              Not now
            </Button>
            {isHappyPath && (
              <Button
                size="sm"
                onClick={onEnable}
                disabled={pending}
                data-testid="push-prompt-enable"
              >
                Enable notifications
              </Button>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-theme-muted hover:text-theme-text"
          aria-label="Close"
          data-testid="push-prompt-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
